/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import MainContainer from "../../layout/MainContainer";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Card,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Button,
  IconButton,
  TableBody,
} from "@mui/material";
import { Loader, showError, showToast } from "../../constants";
import { WarnModal } from "../../components";
import { GetCategories, DeleteCategory } from "../../services/home";

interface Category {
  _id: string;
  name: string;
  description?: string;
  parentCategoryId?: string | null;
  createdAt: string;
}

const ManageCategories = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<string>("");
  const [openWarn, setOpenWarn] = useState(false);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = (await GetCategories()) as { code: number; data: Category[] };
      if (response?.code === 200) {
        setCategories(response.data || []);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const response = (await DeleteCategory(deleteId)) as { code: number };
      if (response?.code === 200) {
        showToast("Category deleted successfully");
        setCategories((prev) => prev.filter((c) => c._id !== deleteId));
      }
    } catch (error) {
      showError("Failed to delete category");
    } finally {
      setLoading(false);
      setOpenWarn(false);
    }
  };

  const parentNameFor = (parentId: string | null | undefined) => {
    if (!parentId) return "—";
    return categories.find((c) => c._id === parentId)?.name ?? "—";
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_loyout">
        <div className="dashboard">
          <div className="heading-bread">
            <h1 className="mn_hdng">Categories</h1>
          </div>
        </div>
        <Card className="cards">
          <Box className="cards_header">
            <Box className="cards_header_right">
              <Button
                variant="contained"
                sx={{ height: 40 }}
                onClick={() => navigate("/manage-categories/add")}
              >
                Add Category
              </Button>
            </Box>
          </Box>
          <TableContainer className="table_container">
            <Table sx={{ minWidth: 650 }} aria-label="categories table">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>NAME</TableCell>
                  <TableCell>PARENT CATEGORY</TableCell>
                  <TableCell>DESCRIPTION</TableCell>
                  <TableCell>CREATED</TableCell>
                  <TableCell align="right">ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.length ? (
                  categories.map((item, i) => (
                    <TableRow key={item._id}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{parentNameFor(item.parentCategoryId)}</TableCell>
                      <TableCell>{item.description || "—"}</TableCell>
                      <TableCell>{moment(item.createdAt).format("YYYY-MM-DD")}</TableCell>
                      <TableCell align="right">
                        <Box className="table_actions">
                          <IconButton
                            onClick={() =>
                              navigate(`/manage-categories/edit/${item._id}`, {
                                state: { item },
                              })
                            }
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => {
                              setDeleteId(item._id);
                              setOpenWarn(true);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={6} sx={{ color: "#051140" }}>
                      No categories found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </div>
      <WarnModal
        open={openWarn}
        setOpen={setOpenWarn}
        handleDelete={handleDelete}
        name="category"
      />
    </MainContainer>
  );
};

export default ManageCategories;
